import React, { useRef } from 'react';
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Stack,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Upload as UploadIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { LLMTemplate } from '../services/interfaces';
import { VOICE_OPTIONS } from '../consts';

const WEEK_DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const US_TIMEZONES = [
  {
    value: 'America/New_York',
    label:
      'Eastern Time (ET) - AL, CT, DC, DE, FL, GA, IN, KY, MA, MD, ME, MI, NC, NH, NJ, NY, OH, PA, RI, SC, TN, VA, VT, WV',
  },
  {
    value: 'America/Chicago',
    label:
      'Central Time (CT) - AL, AR, IA, IL, KS, LA, MN, MO, MS, ND, NE, OK, SD, TN, TX, WI',
  },
  {
    value: 'America/Denver',
    label: 'Mountain Time (MT) - AZ, CO, ID, MT, NM, UT, WY',
  },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - CA, NV, OR, WA' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) - AK' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT) - HI' },
];

const HOURS = [
  '12:00 AM',
  '01:00 AM',
  '02:00 AM',
  '03:00 AM',
  '04:00 AM',
  '05:00 AM',
  '06:00 AM',
  '07:00 AM',
  '08:00 AM',
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '01:00 PM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
  '05:00 PM',
  '06:00 PM',
  '07:00 PM',
  '08:00 PM',
  '09:00 PM',
  '10:00 PM',
  '11:00 PM',
];

interface CreateCampaignModalProps {
  open: boolean;
  campaignName: string;
  campaignDescription: string;
  selectedFile: File | null;
  selectedTemplateId: string;
  templates: LLMTemplate[];
  templatesLoading: boolean;
  businessDays: Record<string, boolean>;
  startTime: string;
  endTime: string;
  timezone: string;
  maxCallDurationSec: number;
  ttsVoiceModel: string;
  isLoading: boolean;
  canCreate: boolean;
  onClose: () => void;
  onCreate: () => void;
  onCampaignNameChange: (value: string) => void;
  onCampaignDescriptionChange: (value: string) => void;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  onTemplateIdChange: (value: string) => void;
  onBusinessDaysChange: (days: Record<string, boolean>) => void;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onTimezoneChange: (value: string) => void;
  onMaxCallDurationSecChange: (value: number) => void;
  onTtsVoiceModelChange: (value: string) => void;
}

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = (
  props
) => {
  const {
    open,
    campaignName,
    campaignDescription,
    selectedFile,
    selectedTemplateId,
    templates,
    templatesLoading,
    businessDays,
    startTime,
    endTime,
    timezone,
    maxCallDurationSec,
    ttsVoiceModel,
    isLoading,
    canCreate,
    onClose,
    onCreate,
    onCampaignNameChange,
    onCampaignDescriptionChange,
    onFileSelect,
    onFileRemove,
    onTemplateIdChange,
    onBusinessDaysChange,
    onStartTimeChange,
    onEndTimeChange,
    onTimezoneChange,
    onMaxCallDurationSecChange,
    onTtsVoiceModelChange,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
    event.target.value = '';
  };

  const handleBusinessDayChange = (key: string, checked: boolean) => {
    onBusinessDaysChange({
      ...businessDays,
      [key]: checked,
    });
  };

  const errors = {
    name: !campaignName.trim() ? 'Name is required' : '',
    maxCallDurationSec:
      !maxCallDurationSec || maxCallDurationSec <= 0
        ? 'Must be greater than 0'
        : '',
    ttsVoiceModel: !ttsVoiceModel ? 'Voice Model is required' : '',
    template: !selectedTemplateId ? 'Template is required' : '',
    file: !selectedFile ? 'Contacts file is required' : '',
    startTime: !startTime ? 'Start Time is required' : '',
    endTime: !endTime ? 'End Time is required' : '',
    businessDays: !Object.values(businessDays).some(Boolean)
      ? 'Select at least one business day'
      : '',
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create Campaign</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{ mt: 1 }}
          >
            <TextField
              fullWidth
              required
              label='Name'
              placeholder='Enter campaign name'
              value={campaignName}
              onChange={(e) => onCampaignNameChange(e.target.value)}
              variant='outlined'
              size='small'
              error={!!errors.name}
              helperText={errors.name}
            />

            <TextField
              fullWidth
              label='Description'
              placeholder='Enter campaign description'
              value={campaignDescription}
              onChange={(e) => onCampaignDescriptionChange(e.target.value)}
              variant='outlined'
              size='small'
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              required
              label='Max Call Duration (sec)'
              type='number'
              placeholder='Enter max call duration in seconds'
              value={maxCallDurationSec}
              onChange={(e) =>
                onMaxCallDurationSecChange(Number(e.target.value))
              }
              variant='outlined'
              size='small'
              error={!!errors.maxCallDurationSec}
              helperText={errors.maxCallDurationSec}
            />

            {/* Voice Model selection */}
            <FormControl
              fullWidth
              size='small'
              required
              error={!!errors.ttsVoiceModel}
            >
              <InputLabel>Voice Model</InputLabel>
              <Select
                value={ttsVoiceModel}
                label='Voice Model'
                onChange={(e) => onTtsVoiceModelChange(e.target.value)}
              >
                {VOICE_OPTIONS.map((voice) => (
                  <MenuItem
                    key={voice.value}
                    value={voice.value}
                  >
                    {voice.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.ttsVoiceModel && (
                <FormHelperText>{errors.ttsVoiceModel}</FormHelperText>
              )}
            </FormControl>

            {/* Template selection */}
            <FormControl
              fullWidth
              size='small'
              required
              error={!!errors.template}
            >
              <InputLabel>Template</InputLabel>
              <Select
                value={selectedTemplateId}
                label='Template'
                onChange={(e) => onTemplateIdChange(e.target.value)}
                disabled={templatesLoading}
              >
                {templatesLoading ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  templates.map((template) => (
                    <MenuItem
                      key={template.id}
                      value={template.id}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {template.name}
                        {template.isDefault && (
                          <Chip
                            label='Default'
                            size='small'
                            color='primary'
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.template && (
                <FormHelperText>{errors.template}</FormHelperText>
              )}
            </FormControl>

            {/* File info and Upload button */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2,
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                pt: 1,
              }}
            >
              {selectedFile ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <FileIcon color='primary' />
                  <Box>
                    <Typography
                      variant='body2'
                      noWrap
                      sx={{ maxWidth: isMobile ? 200 : 300 }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <Button
                    size='small'
                    onClick={onFileRemove}
                    sx={{ ml: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Typography
                  variant='body2'
                  color={errors.file ? 'error' : 'text.secondary'}
                >
                  {errors.file || 'Upload contacts to create a campaign'}
                </Typography>
              )}
              <Button
                variant='outlined'
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                disabled={isLoading}
                fullWidth={isMobile}
              >
                Upload
              </Button>
            </Box>

            {/* Business Days */}
            <Box sx={{ pt: 2 }}>
              <Typography
                variant='subtitle2'
                gutterBottom
              >
                Business Days
              </Typography>
              <FormGroup row>
                {WEEK_DAYS.map((day) => (
                  <FormControlLabel
                    key={day.key}
                    control={
                      <Checkbox
                        checked={businessDays[day.key]}
                        onChange={(e) =>
                          handleBusinessDayChange(day.key, e.target.checked)
                        }
                        size='small'
                      />
                    }
                    label={<Typography variant='body2'>{day.label}</Typography>}
                  />
                ))}
              </FormGroup>
              {errors.businessDays && (
                <FormHelperText error>{errors.businessDays}</FormHelperText>
              )}
            </Box>

            {/* Business Hours */}
            <Box sx={{ pt: 1 }}>
              <Typography
                variant='subtitle2'
                gutterBottom
              >
                Business Hours
              </Typography>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
              >
                <FormControl
                  fullWidth
                  size='small'
                  error={!!errors.startTime}
                >
                  <InputLabel>Start Time</InputLabel>
                  <Select
                    value={startTime}
                    label='Start Time'
                    onChange={(e) => onStartTimeChange(e.target.value)}
                  >
                    {HOURS.map((hour) => (
                      <MenuItem
                        key={hour}
                        value={hour}
                      >
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.startTime && (
                    <FormHelperText>{errors.startTime}</FormHelperText>
                  )}
                </FormControl>
                <Typography variant='body2'>-</Typography>
                <FormControl
                  fullWidth
                  size='small'
                  error={!!errors.endTime}
                >
                  <InputLabel>End Time</InputLabel>
                  <Select
                    value={endTime}
                    label='End Time'
                    onChange={(e) => onEndTimeChange(e.target.value)}
                  >
                    {HOURS.map((hour) => (
                      <MenuItem
                        key={hour}
                        value={hour}
                      >
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.endTime && (
                    <FormHelperText>{errors.endTime}</FormHelperText>
                  )}
                </FormControl>
              </Stack>
            </Box>

            {/* Timezone */}
            <Box sx={{ pt: 1 }}>
              <FormControl
                fullWidth
                size='small'
              >
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={timezone}
                  label='Timezone'
                  onChange={(e) => onTimezoneChange(e.target.value)}
                  disabled
                >
                  {US_TIMEZONES.map((tz) => (
                    <MenuItem
                      key={tz.value}
                      value={tz.value}
                    >
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            onClick={onCreate}
            loading={isLoading}
            disabled={!canCreate}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept='.xlsx,.xls'
      />
    </>
  );
};
